import { CommentWithUnread } from './commentUtils';

export interface LocationCommentGroup {
  locationId?: string;
  commercialName?: string;
  comments?: Record<string, CommentWithUnread[]>;
}

export interface CommentThreadSources {
  taskComments?: Record<string, CommentWithUnread[]>;
  mediaPlanComments?: Record<string, CommentWithUnread[]>;
  attachmentComments?: Record<string, CommentWithUnread[]>;
  orderComments?: LocationCommentGroup[];
  servicesOrderComments?: LocationCommentGroup[];
}

export interface CommentThreadContext {
  taskId?: string;
  attachmentId?: string;
  mediaPlanId?: string;
  orderId?: string;
  locationId?: string;
  useServicesEndpoints?: boolean;
}

export interface CommentTab {
  locationId?: string;
  commercialName?: string;
  hasUnread: boolean;
}

/** The per-location tab list for order comments, each flagged with any-unread. */
export const buildCommentTabs = (orderComments?: LocationCommentGroup[]): CommentTab[] => {
  if (orderComments && orderComments.length) {
    return orderComments.map((comment) => ({
      locationId: comment.locationId,
      commercialName: comment.commercialName,
      hasUnread: Object.values(comment.comments ?? {})
        .flat()
        .some((c) => c.isUnread),
    }));
  }

  return [];
};

/**
 * Picks the active comment record from whichever entity the panel is scoped to.
 * Precedence: task (non-attachment) → media plan → task attachment → services-order
 * → order (by locationId, else the first location group).
 */
const selectCommentSource = (
  {
    taskComments,
    mediaPlanComments,
    attachmentComments,
    orderComments,
    servicesOrderComments,
  }: CommentThreadSources,
  {
    taskId,
    attachmentId,
    mediaPlanId,
    orderId,
    locationId,
    useServicesEndpoints,
  }: CommentThreadContext,
): Record<string, CommentWithUnread[]> | undefined => {
  if (taskId && !attachmentId && taskComments && Object.entries(taskComments)?.length) {
    return taskComments;
  }
  if (mediaPlanId && mediaPlanComments && Object.entries(mediaPlanComments)?.length) {
    return mediaPlanComments;
  }
  if (taskId && attachmentId && attachmentComments && Object.entries(attachmentComments)?.length) {
    return attachmentComments;
  }
  if (orderId && useServicesEndpoints) {
    return servicesOrderComments?.[0]?.comments;
  }
  if (orderId) {
    const matched = locationId
      ? orderComments?.find((comment) => comment.locationId === locationId)?.comments
      : undefined;
    return matched ?? orderComments?.[0]?.comments;
  }
  return undefined;
};

/** Orders comment threads by their most recent comment (oldest thread first). */
const sortThreadsByRecency = (
  comments: Record<string, CommentWithUnread[]>,
): Record<string, CommentWithUnread[]> => {
  const sortedKeys = Object.entries(comments)
    .sort(([, a], [, b]) => {
      const maxDate = (arr: CommentWithUnread[]) =>
        arr.reduce((max, c) => {
          const t = c.created ? new Date(c.created).getTime() : 0;
          return t > max ? t : max;
        }, 0);
      return maxDate(a) - maxDate(b);
    })
    .map(([key]) => key);

  return sortedKeys.reduce(
    (acc, key) => {
      acc[key] = comments[key];
      return acc;
    },
    {} as Record<string, CommentWithUnread[]>,
  );
};

/** Selects the scoped comment record and returns its threads sorted by recency. */
export const buildCommentThreads = (
  sources: CommentThreadSources,
  context: CommentThreadContext,
): Record<string, CommentWithUnread[]> | undefined => {
  const comments = selectCommentSource(sources, context);
  if (!comments) return undefined;
  return sortThreadsByRecency(comments);
};
